/**
 * Composable for Referral Program
 * Follows DRY principles and Vue.js Style Guide
 * 
 * ✅ ОБНОВЛЕНО: Использует Pinia store для хранения состояния
 */

import { ref, computed } from 'vue';
import { consola } from 'consola';
import { useReferralStore , apiReferralInstance } from '@entities/referral';
import type {
  ApplyReferralCodeDto,
  ReferralAnalyticsResponseDto,
} from '@entities/referral';


const logger = consola.withTag('use-referral');

/**
 * Composable для управления реферальной программой
 * ✅ Использует Pinia store для централизованного управления состоянием
 */
export const useReferral = () => {
  // Используем store
  const referralStore = useReferralStore();
  
  // Аналитика (не хранится в store, используется только на странице)
  const analytics = ref<ReferralAnalyticsResponseDto | null>(null);
  
  // ============================================
  // Computed (из store)
  // ============================================
  
  const loading = computed(() => referralStore.loading);
  const error = computed(() => referralStore.error);
  const referralCode = computed(() => referralStore.referralCode);
  const stats = computed(() => referralStore.stats);
  const hasInsufficientOrders = computed(() => referralStore.hasInsufficientOrders);
  const hasReferralCode = computed(() => referralStore.hasReferralCode);
  const isReferralActive = computed(() => referralStore.isReferralActive);
  const canShowReferral = computed(() => referralStore.canShowReferral);
  const canCreateReferralCode = computed(() => !referralStore.hasInsufficientOrders);
  const totalEarned = computed(() => referralStore.totalPointsEarned);
  const totalReferrals = computed(() => referralStore.totalReferrals);
  const activeReferrals = computed(() => referralStore.activeReferrals);
  const completedReferrals = computed(() => referralStore.completedReferrals);
  const promocode = computed(() => referralStore.promocode);
  const isEmpty = computed(() => referralStore.totalReferrals === 0);
  
  // ============================================
  // Methods
  // ============================================
  
  /**
   * Получить или создать реферальный код
   * ✅ Сохраняет данные в store
   */
  const fetchReferralCode = async (): Promise<void> => {
    if (loading.value) return;
    
    try {
      referralStore.setLoading(true);
      referralStore.setError(null);
      referralStore.setInsufficientOrders(false);
      
      if (process.dev) {
        logger.debug('fetchReferralCode called');
      }
      
      const response = await apiReferralInstance.getMyReferralCode();

      // Отказ приезжает НЕ ошибкой: бэк отвечает HTTP 200 с телом
      // `{success:false, code:'INSUFFICIENT_ORDERS', message:'…'}`, поэтому catch ниже
      // (он ловит только 403) не срабатывал. Раньше такой ответ клался в стор как есть,
      // и гость видел на экране служебный код `INSUFFICIENT_ORDERS` вместо объяснения.
      if ((response as { success?: boolean })?.success === false) {
        const failure = response as { code?: string; message?: string };
        referralStore.setInsufficientOrders(failure.code === 'INSUFFICIENT_ORDERS');
        referralStore.setError(failure.message || 'Реферальная ссылка пока недоступна');
        referralStore.setLoading(false);
        return;
      }

      // ✅ Сохраняем в store
      referralStore.setReferralCode(response);

      if (process.dev) {
        logger.debug('Referral code fetched:', {
          code: response.code,
          isActive: response.isActive,
          usageCount: response.usageCount,
        });
      }
    } catch (error_: any) {
      // Проверяем, если ошибка 403 - недостаточно покупок
      if (error_?.statusCode === 403 || error_?.status === 403) {
        referralStore.setInsufficientOrders(true);
        referralStore.setError(error_?.data?.message || 'Для получения реферальной ссылки необходимо совершить минимум 2 покупки');
      } else {
        referralStore.setError(error_?.data?.message || 'Ошибка при получении реферального кода');
      }
      
      logger.error('Ошибка получения реферального кода:', error_);
      referralStore.setLoading(false);
      throw error_;
    }
    referralStore.setLoading(false);
  };
  
  /**
   * Применить реферальный код
   */
  const applyReferralCode = async (data: ApplyReferralCodeDto): Promise<boolean> => {
    try {
      referralStore.setLoading(true);
      referralStore.setError(null);
      
      if (process.dev) {
        logger.debug('applyReferralCode called');
      }
      
      await apiReferralInstance.applyReferralCode(data);
      
      if (process.dev) {
        logger.debug('Referral code applied successfully');
      }

      referralStore.setLoading(false);
      return true;
    } catch (error_: any) {
      referralStore.setError(error_?.data?.message || 'Ошибка при применении реферального кода');
      logger.error('Ошибка применения реферального кода:', error_);
      referralStore.setLoading(false);
      return false;
    }
  };
  
  /**
   * Получить статистику
   * ✅ Сохраняет данные в store
   * ✅ Может выполняться параллельно с fetchReferralCode()
   */
  const fetchStats = async (): Promise<void> => {
    // ✅ Убрали проверку loading.value, чтобы не блокировать параллельные запросы
    // Promise.allSettled() в init() обрабатывает параллельное выполнение
    
    try {
      // Не устанавливаем общий loading, чтобы не блокировать другие запросы
      // referralStore.setLoading(true); // ❌ Убрано для параллельного выполнения
      
      if (process.dev) {
        logger.debug('fetchStats called');
      }
      
      const response = await apiReferralInstance.getReferralStats();
      
      // ✅ Сохраняем в store
      referralStore.setStats(response);
      
      if (process.dev) {
        logger.debug('Stats fetched:', {
          totalReferrals: response.totalReferrals,
          totalPointsEarned: response.totalPointsEarned,
          activeReferrals: response.activeReferrals,
          completedReferrals: response.completedReferrals,
        });
      }
    } catch (error_: any) {
      // Не перезаписываем общую ошибку, если она уже установлена
      if (!referralStore.error) {
        referralStore.setError(error_?.data?.message || 'Ошибка при получении статистики');
      }
      logger.error('Ошибка получения статистики:', error_);
      // Не пробрасываем ошибку дальше, чтобы не блокировать другие запросы в Promise.allSettled
    }
    // ✅ Убрали finally с setLoading(false), так как не устанавливали loading
  };
  
  /**
   * Получить аналитику
   */
  const fetchAnalytics = async (startDate?: string, endDate?: string): Promise<void> => {
    if (loading.value) return;
    
    try {
      referralStore.setLoading(true);
      referralStore.setError(null);
      
      if (process.dev) {
        logger.debug('fetchAnalytics called', { startDate, endDate });
      }
      
      const response = await apiReferralInstance.getReferralAnalytics({
        startDate,
        endDate,
      });
      
      // Аналитика не сохраняется в store (используется только на странице)
      analytics.value = response;
      
      if (process.dev) {
        logger.debug('Analytics fetched:', {
          totalReferrals: response.totalReferrals,
          refereesCount: response.referees.length,
        });
      }
    } catch (error_: any) {
      referralStore.setError(error_?.data?.message || 'Ошибка при получении аналитики');
      logger.error('Ошибка получения аналитики:', error_);
      referralStore.setLoading(false);
      throw error_;
    }
    referralStore.setLoading(false);
  };
  
  /**
   * Валидация токена
   */
  const validateToken = async (token: string): Promise<boolean> => {
    try {
      if (process.dev) {
        logger.debug('validateToken called', { token });
      }
      
      const response = await apiReferralInstance.validateToken(token);
      return response.isValid;
    } catch (error_: any) {
      logger.error('Ошибка валидации токена:', error_);
      return false;
    }
  };
  
  /**
   * Сброс состояния
   * ✅ Использует store reset
   */
  const reset = (): void => {
    referralStore.reset();
    analytics.value = null;
  };
  
  /**
   * Инициализация - загрузка кода и статистики
   */
  const init = async (): Promise<void> => {
    try {
      // Загружаем код и статистику параллельно
      await Promise.allSettled([
        fetchReferralCode(),
        fetchStats(),
      ]);
    } catch (error_) {
      logger.error('Ошибка инициализации:', error_);
    }
  };
  
  // ============================================
  // Return
  // ============================================
  
  return {
    // State (из store)
    loading,
    error,
    referralCode,
    stats,
    analytics,
    hasInsufficientOrders,
    
    // Computed (из store)
    hasReferralCode,
    isReferralActive, // ✅ Проверка активности программы
    canShowReferral, // ✅ Учитывает isActive
    canCreateReferralCode,
    totalEarned,
    totalReferrals,
    activeReferrals, // ✅ Количество активных рефералов
    completedReferrals, // ✅ Количество завершенных рефералов
    promocode, // ✅ Промокод из статистики
    isEmpty,
    
    // Methods
    fetchReferralCode,
    applyReferralCode,
    fetchStats,
    fetchAnalytics,
    validateToken,
    reset,
    init,
  };
};
